<template>
  <MasterModalBase
    show-modal
    full-screen
    width-xl
    :navigating="navigating"
    :action-buttons="actionButtons"
    :main-transition-key="invoiceId"
    :overlay-scrollb="stage !== 'create'"
    :full-h="stage !== 'create'"
    class="invoice-master-modal"
    @close-modal="close"
  >
    <template v-slot:title>
      <span v-if="invoice && !isCorrection">{{ modalTitle }} {{ invoice.number }}</span>
      <span v-if="invoice && isCorrection">{{ modalTitle }} {{ invoice.correction.name }}</span>
      <span v-if="!invoice">{{ $gettext('New') }} {{ modalTitle }}</span>
    </template>
    <template v-slot:status>
      <div class="invoice-top-badges--wrap">
        <StatusBadge v-if="invoice" :display-status="invoice.display_status" />
      </div>
    </template>
    <template v-slot:buttons>
      <template v-if="stage !== 'create' && invoice && invoice.status !== 'recurrent'">
        <div class="buttons-wrap">
          <div id="tt-invmodal-edit" class="buttons-tt-wrap">
            <GekkoButton
              v-if="invoiceIdSet"
              @click.prevent="loadView('edit')"
              :disabled="invoice.status !== 'd' && !isCorrection && !isUploaded"
              :text="$gettext('Edit')"
              :icon="'fa-regular fa-pen-to-square'"
              :variant="'modal-tab'"
              :class="(stage === 'edit') ? 'active' : ''"
            />
          </div>
          <b-tooltip v-if="invoice.status !== 'd' && !isCorrection" target="tt-invmodal-edit" key="tt-invmodal-edit" placement="bottom" triggers="hover">
            <div class="tooltip-text">
              {{ $gettext('Editing is disabled for sent invoices') }}
            </div>
          </b-tooltip>
        </div>
        <div class="buttons-wrap">
          <div id="tt-invmodal-view" class="buttons-tt-wrap">
            <GekkoButton
              @click.prevent="loadView('view')"
              :disabled="!invoiceIdSet"
              :text="$gettext('View')"
              :icon="'fa-regular fa-eye'"
              :variant="'modal-tab'"
              :class="(stage === 'view') ? 'active' : ''"
            />
          </div>
          <b-tooltip v-if="!invoiceIdSet" target="tt-invmodal-view" key="tt-invmodal-view" placement="bottom" triggers="hover">
            <div class="tooltip-text">
              {{ $gettext('Invoice is not saved') }}
            </div>
          </b-tooltip>
        </div>
        <div class="buttons-wrap">
          <div id="tt-invmodal-send" class="buttons-tt-wrap">
            <GekkoButton
              @click.prevent="loadView('send')"
              :disabled="!invoiceIdSet"
              :text="$gettext('Send')"
              :icon="'fa-regular fa-envelope'"
              :variant="'modal-tab'"
              :class="(stage === 'send') ? 'active' : ''"
            />
          </div>
          <b-tooltip v-if="!invoiceIdSet" target="tt-invmodal-send" key="tt-invmodal-send" placement="bottom" triggers="hover">
            <div class="tooltip-text">
              {{ $gettext('Invoice is not saved') }}
            </div>
          </b-tooltip>
        </div>
        <div class="buttons-wrap" v-if="!isCorrection">
          <GekkoButton
            @click.prevent="loadView('payments')"
            :text="$gettext('Payments')"
            :icon="'fa-regular fa-credit-card'"
            :variant="'modal-tab'"
            :class="(stage === 'payments') ? 'active' : ''"
          />
        </div>
      </template>
    </template>
    <template v-if="invoice && stage !== 'create' && invoice.status !== 'recurrent'" v-slot:options>

      <div v-if="stage !== 'create' && (prevNext.prev || prevNext.next)" class="invoice-top-btns--wrap">
        <div :class="{ 'nav-disabled': !prevNext.prev }">
          <GekkoButton 
            @click="navigateTo(prevNext.prev, 'prev')" 
            icon="fa-solid fa-arrow-left-long"
            variant="link"
            :disabled="!prevNext.prev"
            class="p-0"
          />
        </div>
        <div :class="{ 'nav-disabled': !prevNext.next }">
          <GekkoButton 
            @click="navigateTo(prevNext.next, 'next')" 
            icon="fa-solid fa-arrow-right-long"
            variant="link"
            :disabled="!prevNext.next"
            class="p-0"
          />
        </div>
      </div>


      <b-dropdown class="modal-topbar-icon-toggle ui-dashboard-menu-dropdown" :key="'options-' + invoice.id" variant="outline-primary" right no-caret>
        <template #button-content>
          <font-awesome-icon icon="fa-solid fa-ellipsis-vertical" />
        </template>

        <b-dropdown-item :href="getUrl('invoices_pdf', invoice.id)" :title="$gettext('download invoice')">
          <font-awesome-icon icon="fa-solid fa-download" />
          <span v-translate>Download</span>
        </b-dropdown-item>

        <b-dropdown-item v-if="!isCorrection && !isUploaded" :href="getUrl('invoices_convert_ubl', invoice.id)" :title="$gettext('UBL (Universal Business Language) is a European standard invoicing format used by various accounting software.')">
          <font-awesome-icon icon="fa-solid fa-download" />
          <span v-translate>Download UBL</span>
        </b-dropdown-item>

        <b-dropdown-item v-if="!invoice.uploaded && !isCorrection" @click="setInvoiceToCopy(invoice)">
          <font-awesome-icon icon="fa-regular fa-copy" />
          <span v-translate>Copy invoice</span>
        </b-dropdown-item>

        <b-dropdown-item v-if="!isCorrection && !isUploaded && invoice.display_status && creditableStatuses.includes(invoice.display_status.detailed_status)" @click="creditInvoice(invoice.id)">
          <font-awesome-icon icon="fa-solid fa-rotate-left" />
          <span v-translate>Credit invoice</span>
        </b-dropdown-item>

        <b-dropdown-item v-if="invoice.status === 'd'" :href="getUrl('accounts_invoice_design_settings')" :title="$gettext('Invoice design')">
          <font-awesome-icon icon="fa-solid fa-paintbrush" />
          <span v-translate>Design your invoice</span>
        </b-dropdown-item>
        <DropdownItemDelete
          :key="'dd-delete-' + invoice.id"
          :item="invoice"
          @invoice-deleted="onInvoiceDelete"
        />
      </b-dropdown>
    </template>
    <template v-slot:maincontent>
      <div class="master-invoice-modal-view-wrap">
        <LoadingSpinner v-if="loading" />
        <div v-else class="master-invoice-modal-view">
          <div v-if="stage === 'create'" class="view-wrap" :class="['view-wrap--' + stage]">
            <InvoiceAdd
              ref="invoice-master-add"
              @display-buttons="showActionButtons"
              @invoice-updated="setListRefresh"
            />
          </div>
          <div v-if="stage === 'edit'" class="view-wrap" :class="['view-wrap--' + stage]">
            <InvoiceAdd
              v-if="!isCorrection && !isUploaded"
              is-edit
              ref="invoice-master-add"
              @display-buttons="showActionButtons"
              @invoice-updated="setListRefresh"
            />
            <EditCorrectionView
              v-if="isCorrection"
              ref="correction-master-edit"
              @display-buttons="showActionButtons"
              @correction-updated="setListRefresh"
            />
            <EditInvoiceUploadView
              v-if="isUploaded"
              ref="invoice-uploaded-master-edit"
              @display-buttons="showActionButtons"
              @invoice-updated="setListRefresh"
            />
          </div>
          <div v-if="stage === 'view'" class="view-wrap" :class="['view-wrap--' + stage]">
            <InvoiceView
              ref="invoice-master-view"
              @display-buttons="showActionButtons"
              @invoice-updated="setListRefresh"
            />
          </div>
          <div v-if="stage === 'send'" class="view-wrap" :class="['view-wrap--' + stage]">
            <InvoiceSend
              ref="invoice-master-send"
              @display-buttons="showActionButtons"
              @invoice-updated="setListRefresh"
            />
          </div>
          <div v-if="stage === 'payments'" class="view-wrap" :class="['view-wrap--' + stage]">
            <InvoicePayments
              ref="invoice-master-payments"
              :invoice="invoice" 
              @display-buttons="showActionButtons"
              @invoice-updated="setListRefresh"
              :key="invoiceId"
            />
          </div>
        </div>
      </div>
    </template>
    <template v-if="actionButtons && actionButtons != ''" v-slot:actions>

    <!-- VIEW INVOICE -->
      <div v-if="invoice && actionButtons === 'view'" class="d-flex justify-content-end" style="gap: 0.5rem;">
        <!-- schedule summary -->
        <div v-if="scheduleSummary" class="recurrent-summary on-modal" @click="$store.commit('invoicesRecurrent/TOGGLE_MODAL')">
          <font-awesome-icon icon="fa-solid fa-clock-rotate-left" />
          {{ scheduleSummary }}
        </div>
        <!-- schedule -->
        <GekkoButton
          v-if="invoice.status === 'd' && !scheduleSummary"
          @click="$store.commit('invoicesRecurrent/TOGGLE_MODAL')"
          :text="$gettext('Schedule')"
          icon="fa-solid fa-clock-rotate-left"
          variant="outline-primary"
        />
        <!-- rabobank widget -->
        <GekkoButton
          v-if="$refs['invoice-master-view'] && $refs['invoice-master-view'].allowRaboFinancing"
          @click="$refs['invoice-master-view'].manualFinanceToggle()"
          :text="$gettext('Finance Invoice')"
          icon="fa-solid fa-credit-card"
          class="mr-auto"
        />
        <!-- payments -->
        <GekkoButton
          v-if="(invoice.status === 's' || invoice.status === 'p') && !isCorrection"
          :to="{ name: 'InvoicePayments', params: { invoiceId: invoiceId } }"
          :text="(invoice.status === 's') ? $gettext('Add payment') : $gettext('View payments')"
          icon="fa-solid fa-credit-card"
          :variant="(invoice.status === 'p') ? 'outline-primary' : 'primary'"
        />
        <!-- send -->
        <GekkoButton 
          v-if="(invoice && invoice.status === 's' && !isUploaded)"
          :to="{ name: 'InvoiceSendNew', params: { invoiceId: invoiceId } }"
          :text="$gettext('Send reminder')"
          icon="fa-solid fa-envelope"
          variant="outline-primary"
        />
        <GekkoButton 
          v-else
          :to="{ name: 'InvoiceSendNew', params: { invoiceId: invoiceId } }"
          :text="$gettext('Send')"
          icon="fa-solid fa-envelope"
        />
        <!-- edit -->
        <GekkoButton
          v-if="invoice.status === 'd' || isCorrection || isUploaded"
          :to="{ name: 'InvoiceEdit', params: { invoiceId: invoiceId } }"
          :text="$gettext('Edit')"
          icon="fa-solid fa-pencil"
          variant="outline-primary"
        />
        <!-- mark as sent -->
        <GekkoButton
          v-if="invoice.status === 'd'"
          @click="$refs['invoice-master-view'].markAsSent()"
          :text="$gettext('Mark as sent')"
          icon="fa-solid fa-check-square"
          variant="outline-primary"
        />
      </div>

    <!-- SEND INVOICE -->
      <div v-if="invoice && actionButtons === 'send' && $refs['invoice-master-send']" class="d-flex justify-content-end" style="gap: 0.5rem;">
        <!-- mark as sent -->
        <GekkoButton 
          @click="$refs['invoice-master-send'].markAsSent()"
          :disabled="$refs['invoice-master-send'].uploadingFiles || $refs['invoice-master-send'].isSending"
          :text="$gettext('Mark as sent')"
          icon="fa-solid fa-check-square"
        />
        <!-- send invoice -->
        <GekkoButton 
          @click="$refs['invoice-master-send'].adjustAndSend()"
          :disabled="$refs['invoice-master-send'].uploadingFiles || $refs['invoice-master-send'].isSending"
          :text="$gettext('Send invoice')"
          icon="fa-solid fa-paper-plane"
          type="submit"
        />
      </div>

    <!-- EDIT CORRECTION -->
      <div v-if="actionButtons === 'create' && isCorrection" class="d-flex justify-content-end">
        <!-- update correction -->
        <GekkoButton
          :text="$gettext('Update correction')"
          icon="fa-solid fa-floppy-disk"
          variant="primary"
          @click="$refs['correction-master-edit'].saveCorrection()"
        />
      </div>

    <!-- EDIT UPLOADED INVOICE -->
      <div v-if="actionButtons === 'create' && isUploaded" class="d-flex justify-content-end">
        <!-- update correction -->
        <GekkoButton
          :text="$gettext('Update invoice')"
          icon="fa-solid fa-floppy-disk"
          variant="primary"
          @click="$refs['invoice-uploaded-master-edit'].saveUploadedInvoice()"
        />
      </div>

    <!-- ADD/EDIT INVOICE -->
      <div v-if="actionButtons === 'create' && !isCorrection && !isUploaded" class="d-flex justify-content-between" style="gap: 0.5rem; align-items: center;">
        <!-- design invoice -->
        <GekkoButton
          @click="$refs['invoice-master-add'].showDesign = true"
          :text="$gettext('Edit design')"
          icon="fa-solid fa-paint-brush"
        />

        <!-- save invoice -->
        <GekkoButton
          :text="$gettext('Save')"
          icon="fa-solid fa-floppy-disk"
          variant="primary"
          @click="$refs['invoice-master-add'].saveInvoice()"
        />
      </div>

    </template>
  </MasterModalBase>
</template>
  
<script>
import { mapGetters, mapActions } from "vuex";

import InvoiceAdd from '@/components/invoices/InvoiceAdd'
import InvoiceView from '@/components/invoices/InvoiceView'
import InvoiceSend from '@/components/invoices/InvoiceSend'
import InvoicePayments from '@/components/invoices/InvoicePayments'

import EditCorrectionView from "@/components/invoices/EditCorrectionView"
import EditInvoiceUploadView from "@/components/invoices/EditInvoiceUploadView"

import GekkoButton from "@/components/GekkoButton"
import MasterModalBase from "@/components/base_components/MasterModalBase"
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
import DropdownItemDelete from "@/components/invoices/ui/DropdownItemDelete"
import StatusBadge from "@/components/invoices/ui/StatusBadge"

import InvoiceService from "@/store/services/invoice.service.js"

import invoices from "@/mixins/invoices"

export default {
  name: "InvoiceMasterModal",
  mixins: [invoices],
  components: {
    MasterModalBase,
    GekkoButton,
    LoadingSpinner,
    InvoiceView,
    InvoiceAdd,
    InvoiceSend,
    InvoicePayments,
    DropdownItemDelete,
    StatusBadge,
    EditCorrectionView,
    EditInvoiceUploadView
  },
  props: {
    stage: {
      type: String,
      default: null
    },
    invoiceId: {
      type: [String, Number],
      default: null
    }
  },
  data () {
    return {
      loading: true,
      actionButtons: null,
      refreshList: false,
      prevNext: { prev: false, next: false },
      currentNext: false,
      useCurrentNext: false,
      navigating: null
    }
  },
  computed: {
    ...mapGetters({ 
      currentCompany: 'main/getCurrentCompany',
      creditableStatuses: 'invoices/getCreditableStatuses',
      invoice: 'invoices/getInvoice',
      scheduleSummary: 'invoicesRecurrent/getSummary',
      tempInvoiceList: 'invoices/getTempInvoiceList',
      initialNext: 'invoices/getTempInvoiceListNext'
    }),
    invoiceIdSet () {
      return (this.invoiceId && this.invoiceId != '')
    },
    isCorrection () {
      return (this.invoice && this.invoice.correction !== null)
    },
    isUploaded () {
      return (this.invoice && this.invoice.uploaded)
    },
    // TODO all quotations
    isQuotation () {
      return false
    },
    modalTitle () {
      let modal_title = this.$gettext('Invoice')
      if (this.isCorrection) {
        modal_title = ''
      }
      if (this.isCorrection) {
        modal_title = this.$gettext('Quotation')
      }
      return modal_title
    },
  },
  watch: {
    invoiceId () {
      this.$nextTick(() => {
        // reset recurrent data because it can be set from outside in RecurrentInvoiceModal
        this.resetInvoiceData()
        this.resetRecurrent()
        this.resetSummary()
        this.checkPrevNext()
        this.setupData()
      })
    }
  },
  created () {
    console.log('master created')
    if (this.stage !== 'create') {
      this.checkPrevNext()
      this.unsubscribe = this.$store.subscribe((mutation) => {
        if (mutation.type === 'invoices/SET_TEMP_INVOICE_LIST') {
          this.checkPrevNext()
        }
      })
    }
    // reset recurrent data because it can be set from outside in RecurrentInvoiceModal
    this.resetRecurrent()
    this.resetSummary()
    this.setupData()
  },
  destroyed () {
    console.log('master destroyed')
    if (this.unsubscribe) {
      this.unsubscribe()
    }
  },
  mounted () {
  },
  methods: {
    ...mapActions({ 
      loadInvoice: "invoices/loadInvoice",
      loadInvoiceRecurrent: "invoices/loadInvoiceRecurrent",
      loadCorrectionFiles: "invoices/loadCorrectionFiles",
      setInvoiceToCopy: "invoices/setInvoiceToCopy",
      resetInvoice: 'invoices/resetInvoice',
      resetInvoiceData: 'invoices/resetInvoiceData',
      resetRecurrent: 'invoicesRecurrent/resetRecurrent',
      resetSummary: 'invoicesRecurrent/resetSummary'
    }),
    close () {
      this.resetInvoice()
      this.resetInvoiceData()
      this.resetSummary()
      if (this.refreshList) {
        this.$store.commit('invoices/REFRESH_INVOICE_LIST')
      }
      this.$emit('close')
      this.$router.push({ name: 'Invoices' }).catch(() => {})
    },
    onInvoiceDelete () {
      this.refreshList = true
      this.close()
    },
    loadView (view) {
      switch (view) {
        case 'create':
          this.$router.push({ name: 'InvoiceAdd' }).catch(() => {})
          break;
        case 'edit':
          this.$router.push({ name: 'InvoiceEdit', params: { invoiceId: this.invoiceId } }).catch(() => {})
          break;
        case 'view':
          this.$router.push({ name: 'InvoiceView', params: { invoiceId: this.invoiceId } }).catch(() => {})
          break;
        case 'send':
          this.$router.push({ name: 'InvoiceSendNew', params: { invoiceId: this.invoiceId } }).catch(() => {})
          break;
        case 'payments':
          this.$router.push({ name: 'InvoicePayments', params: { invoiceId: this.invoiceId } }).catch(() => {})
          break;
      }
    },
    setupData () {
      if (this.stage !== 'create' && this.invoiceId) {
        this.loading = true
        const fnName = (this.$route.meta.recurrent) ? 'loadInvoiceRecurrent': 'loadInvoice'
        this[fnName](this.invoiceId).then(() => {
          if (this.invoice.correction) {
            // load correction files
            this.loadCorrectionFiles(this.invoice.correction.id).then(() => {
              this.loading = false
            })
          } else {
            this.loading = false
          }
        }).catch(() => {
          this.$store.commit('setAlert', {
            title: this.$gettext('Not found'),
            text: this.$gettext('The invoice you are trying to open does not exist'),
            variant: 'warning',
            duration: 4
          })
          this.$router.push({ name: 'Invoices' }).catch(() => {})
        })
      } else {
        this.loading = false
      }
    },
    showActionButtons (payload) {
      this.$nextTick(() => {
        this.actionButtons = payload
      })
    },
    setListRefresh () {
      this.refreshList = true
    },
    async checkPrevNext () {
      // calculate invoice position in store tempInvoiceList to get prev & next
      // first time set it to initial invoice list

      if (this.tempInvoiceList.length > 0) {
        let prev = false, next = false
        const invoicePos = this.tempInvoiceList.findIndex(item => item.id == this.invoiceId)
        if (invoicePos > -1) { // this.invoiceId is in current list
          /**
           * 'next' is most recent invoice, so previous element on that current list,
           * should be already there unless it's first item (most recent)
           */
          next = (invoicePos > 0) ? (invoicePos - 1) : false
          this.prevNext.next = (next || next === 0) ? this.tempInvoiceList[next].id : false
          /**
           * 'prev' is next item on invoice list, could not be there yet, so check for next page.
           *  if not last item on currentList, take next position as 'prev'
           */
          prev = (invoicePos + 1)
          if (invoicePos < (this.tempInvoiceList.length - 1)) {
            this.prevNext.prev = (prev || prev === 0) ? this.tempInvoiceList[prev].id : false
          } else {
            // if last item, check for next pages (if next page is avalable)
            await this.getMoreInvoices()
            this.prevNext.prev = (prev || prev === 0) ? this.tempInvoiceList[prev]?.id : false
          }
        } else { // this.invoiceId not in current list
          this.getMoreInvoices(true)
        }
      } 
    },
    async getMoreInvoices (recurring = false) {
      console.log('getMoreInvoices', recurring)
      // need next tick to wait for initialNext mutation
      this.$nextTick(() => {
        if ((this.initialNext && !this.useCurrentNext) || (this.currentNext && this.useCurrentNext)) {
          if(!this.currentNext) this.currentNext = this.initialNext
          return InvoiceService.loadNextPage(this.currentNext).then((response) => {
            if (response.data.results) {
              this.useCurrentNext = true
              this.currentNext = response.data.next
              this.$store.commit('invoices/SET_TEMP_INVOICE_LIST', this.tempInvoiceList.concat(response.data.results));
              if (recurring) this.checkPrevNext()
            }
          })
        }
      })
    },
    navigateTo (target_id, direction) {
      if (target_id) {
        this.navigating = direction
        this.$router.push({ name: 'InvoiceView', params: { invoiceId: target_id } }).catch(() => {})
      }
    }
  }
}
</script>

<style lang="scss">
.recurrent-summary {
  cursor: pointer;
  padding: 1.5rem;
  margin-bottom: 1rem;
  border: 1px solid $colorBorderDash;
  border-radius: $radiusMd;
  background-color: #f5f5f5;
  color: $colorBlue;
  &.on-modal {
    display: flex;
    flex: 1;
    gap: 0.25rem;
    justify-content: center;
    align-items: center;
    text-align: center;
    background: none;
    border: none;
    padding: 0;
    margin: 0;
  }
}
.invoice-master-modal {
  .gk-master-modal {
    > .gk-modal-content {
      > .action-buttons-wrapper {
        border-top: 1px solid $colorBorderDash;
      }
    }
  }
  .master-invoice-modal-view-wrap {
    width: 100%;
    height: 100%;
    position: relative;
    min-height: 100%;
    .master-invoice-modal-view {
      height: 100%;
      display: block;
      .view-wrap {
        height: 100%;
        padding: 0.5rem;
        &--create, &--edit  { 
          padding: 0rem;
        }
        &--view { padding: 0rem; padding-left: 0.5rem; }
        &--send { padding: 0rem; }
        &--payments { padding: 0rem; }
        @media (min-width: '768px') {
          padding: 1rem;
          &--create, &--edit { padding: 0rem; }
          &--view { padding: 0rem; padding-left: 1rem; }
          &--send { padding: 0; }
          &--payments { padding: 0rem; }
        }
      }
    }
  }
  .empty-customer-warning {
    font-size: 1rem;
    color: $colorUnassigned;
  }
}
</style>

<style lang="scss" scoped>
.invoice-top-badges {
  &--wrap {
    display: flex;
    gap: 1rem;
    white-space: nowrap;
    align-items: center;
  }
}
.invoice-top-btns {
  &--wrap {
    display: flex;
    gap: 0.5rem;
    font-size: 20px;
    color: $colorBrand;
    padding-right: 0.5rem;
    .btn { font-size: 20px; }
    .nav-disabled { 
      // opacity: 0.4;
    }
  }
}
</style>